'use client';
import ReserveSeeNotify from '@/components/shared/ReserveSeeNotify/ReserveSeeNotify';
import Conditional from '@/components/shared/conditional/conditional.component';
import Typography from '@/components/shared/typography/typography.component';
import React, { useMemo, useState } from 'react';
import { useTranslation, useWindowDimensions } from '@/hook';
import {
    allLegalities,
    CalendarBookedCar2,
    carFrame,
    engine,
    HighlightsIcon,
    noMajorAcc,
    sevenDays,
    Speedometer2,
    SylndrCertifiedCheckIcon,
    SylndrCertifiedXIcon,
    TransmissionIcon2,
} from '../../../../../public/Images';
import BadgeWithTooltip, {
    EBadgeWithTooltip,
} from '@/components/shared/badege-with-tooltip/badge-with-tooltip.component';

import Modal from '@/components/shared/modal.component';
import {
    CarDetailsType,
    DisplayedCarInfoInterface,
    HighlightsType,
} from '@/types';

import { formatNumber, getCarCardLocale, numberWithCommas } from '@/utils';
import NotifyMe from '../NotifyMe';
import {
    CarProfileCarName,
    CarProfileCurrency,
    CarProfileInfoContainer,
    CarProfileInstallmentPrice,
    CarProFileMoreInfoContainer,
    CarProFileMoreInfoItemContainer,
    CarProfileMoreInfoText,
    CarProfileSubtitle,
    CarProfileTrim,
} from '../StyledComponents/car-info-section-components';
import CarSoldOverlay from '../SimilarCars/CarSoldOverlay';
import SoldBadge from '@/components/shared/SoldBadge/sold-badge.component';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import ShowroomSection from './ShowroomSection/ShowroomSection';
import colors from '@/theme/colors.module.scss';
import styles from './styles.module.scss';
import { AVAILABLE_LANGUAGES } from '@/constants';
import { FlapperLine } from '@/components/shared/car-details-card-v2/flapper-line.component';
import dynamic from 'next/dynamic';
import CarDetailsSection from '../CarProfileInfo/CarDetailsSection';
import myImageLoader from '@/image.local.loader';
import { getCategoryBadgeConfig } from '../constants';

const PriceBreakdown = dynamic(
    () =>
        import('./priceBreakdownSection/price-breakdown.component').then(
            mod => mod.default
        ),
    {
        ssr: false,
    }
);
const WhatsIncluded = dynamic(
    () =>
        import(
            '@/components/shared/WhatsIncluded/whats-included.component'
        ).then(mod => mod.default),
    {
        ssr: false,
    }
);
const CarGuaranteeItem = ({
    text,
    icon,
    alt,
}: {
    text: string;
    icon: string;
    alt: string;
}) => {
    return (
        <div className="p-2.5 bg-gray-50 flex flex-col md:flex-row gap-2 rounded-lg items-center justify-center text-center w-[100%]">
            <div className="flex-shrink-0">
                <Image width={40} height={40} src={icon} alt={alt} />
            </div>
            <div className="text-gray-800 text-xs font-medium leading-[18px]">
                {text}
            </div>
        </div>
    );
};
export const CarGuarantee = ({
    isRefurbished,
    carDetailsTableRef,
}: {
    isRefurbished?: boolean;
    carDetailsTableRef?: React.RefObject<HTMLDivElement>;
}) => {
    const { t } = useTranslation();
    return (
        <div className="pt-4">
            <div className="text-secondary-500 text-lg font-semibold leading-7 md:text-xl md:font-bold md:leading-[30px] ">
                {t('sylndrCertifiedMeaning')}
            </div>
            <div className="flex flex-row mb-4 gap-2">
                <CarGuaranteeItem
                    text={t('allLegalitiesAreChecked')}
                    icon={allLegalities}
                    alt="all legalities are checked"
                />
                <CarGuaranteeItem
                    text={t('noMajorAccidents')}
                    icon={noMajorAcc}
                    alt="no major accidents"
                />
                <CarGuaranteeItem
                    text={t('7DaysReturn')}
                    icon={sevenDays}
                    alt="seven days return"
                />
            </div>
            <CarDetailsTable
                isRefurbished={isRefurbished}
                t={t}
                carDetailsTableRef={carDetailsTableRef}
            />
        </div>
    );
};

const CarDetailsTable = ({
    isRefurbished,
    t,
    carDetailsTableRef,
}: {
    isRefurbished: boolean;
    t: any;
    carDetailsTableRef?: React.RefObject<HTMLDivElement>;
}) => {
    const mobileData = [
        '',
        t('Lite'),
        t('Certified'),
        t('Owned by Sylndr'),
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        t('Refurbished by Sylndr'),
        <img
            src={myImageLoader({
                src: SylndrCertifiedXIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'X Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        t('Inspected by Sylndr'),
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        t('90 Days Warranty'),
        <img
            src={myImageLoader({
                src: SylndrCertifiedXIcon.src,
                width: 20,
                height: 20,
            })}
            alt="X Icon"
            key={'X Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
    ];

    const desktopData = [
        '',
        t('Owned by Sylndr'),
        t('Refurbished by Sylndr'),
        t('Inspected by Sylndr'),
        t('90 Days Warranty'),
        t('Lite'),
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedXIcon.src,
                width: 20,
                height: 20,
            })}
            alt="X Icon"
            key={'X Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedXIcon.src,
                width: 20,
                height: 20,
            })}
            alt="X Icon"
            key={'X Icon'}
            width={20}
            height={20}
        />,
        t('Certified'),
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
        <img
            src={myImageLoader({
                src: SylndrCertifiedCheckIcon.src,
                width: 20,
                height: 20,
            })}
            alt="Check Icon"
            key={'Check Icon'}
            width={20}
            height={20}
        />,
    ];

    return (
        <div className="pt-4" id="car-details-table" ref={carDetailsTableRef}>
            {/* Mobile: 5 rows x 3 columns - highlight entire column 2 or 3 with border based on isRefurbished */}
            <div className="md:hidden">
                {/* Title for highlighted column */}
                <div className="grid grid-cols-3 mb-1">
                    <div></div>
                    {isRefurbished === false && (
                        <div className="text-center text-xs font-medium text-blue-600 bg-blue-100 p-1 rounded">
                            {t('thisCarIs')}
                        </div>
                    )}
                    {isRefurbished === true && <div></div>}
                    {isRefurbished === true && (
                        <div className="text-center text-xs font-medium text-green-600 bg-green-100 p-1 rounded col-start-3">
                            {t('thisCarIs')}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-3 rounded-lg overflow-hidden border border-gray-200">
                    {mobileData.map((item, index) => {
                        const isFirstRow = index < 3;
                        const column = index % 3;
                        const isSecondColumn = column === 1; // Column 2 (Lite)
                        const isThirdColumn = column === 2; // Column 3 (Certified)

                        // Apply special border classes for highlighted columns
                        let borderClasses = '';
                        let roundedClasses = '';
                        if (isRefurbished === false && isSecondColumn) {
                            // Highlight entire Lite column
                            borderClasses =
                                'border-l-2 border-r-2 border-l-blue-500 border-r-blue-500';
                            if (index < 3)
                                borderClasses +=
                                    ' border-t-2 border-t-blue-500';
                            if (index >= 12)
                                borderClasses +=
                                    ' border-b-2 border-b-blue-500';
                            // Add rounded corners for the top and bottom cells of the column
                            if (index === 1) roundedClasses = 'rounded-t-lg';
                            if (index === 13) roundedClasses = 'rounded-b-lg';
                        } else if (isRefurbished === true && isThirdColumn) {
                            // Highlight entire Certified column
                            borderClasses =
                                'border-l-2 border-r-2 border-l-green-500 border-r-green-500';
                            if (index < 3)
                                borderClasses +=
                                    ' border-t-2 border-t-green-500';
                            if (index >= 12)
                                borderClasses +=
                                    ' border-b-2 border-b-green-500';
                            // Add rounded corners for the top and bottom cells of the column
                            if (index === 2) roundedClasses = 'rounded-t-lg';
                            if (index === 14) roundedClasses = 'rounded-b-lg';
                        }

                        return (
                            <div
                                key={index}
                                className={`p-2 text-center text-xs font-medium text-gray-800 flex justify-center ${
                                    isFirstRow ? 'bg-gray-50' : 'bg-white'
                                } ${borderClasses} ${roundedClasses}`}
                            >
                                {item}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Desktop: 3 rows x 5 columns - highlight only the correct row based on isRefurbished */}
            <div className="hidden md:block">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {[0, 1, 2].map(row => {
                        const isSecondRow = row === 1;
                        const isThirdRow = row === 2;
                        const isLite = isRefurbished === false && isSecondRow;
                        const isCertified =
                            isRefurbished === true && isThirdRow;
                        let rowBg = '';
                        let rowBorder = '';
                        if (isLite) {
                            rowBg = 'bg-blue-100';
                            rowBorder = 'border-4 border-blue-500';
                        } else if (isCertified) {
                            rowBg = 'bg-green-100';
                            rowBorder = 'border-4 border-green-500';
                        }
                        return (
                            <div
                                key={row}
                                className={`grid grid-cols-5 relative ${
                                    isLite || isCertified
                                        ? `${rowBg} ${rowBorder} shadow-lg`
                                        : ''
                                }`}
                                style={{
                                    zIndex:
                                        isLite || isCertified ? 1 : undefined,
                                }}
                            >
                                {Array.from({ length: 5 }).map((_, col) => {
                                    const index = row * 5 + col;
                                    const isFirstCell = col === 0;
                                    // Title above highlighted row
                                    const showTitle =
                                        (isLite && isFirstCell) ||
                                        (isCertified && isFirstCell);
                                    return (
                                        <div
                                            key={col}
                                            className={`relative p-3 text-center text-sm font-medium text-gray-800 flex justify-center ${
                                                isFirstCell
                                                    ? 'bg-gray-50 border-r border-gray-200'
                                                    : 'bg-white border-r border-gray-200'
                                            }`}
                                        >
                                            {showTitle && (
                                                <div
                                                    className={`absolute -top-4 left-1/2 transform -translate-x-1/2 text-xs font-medium ${
                                                        isLite
                                                            ? 'text-blue-600 bg-blue-100'
                                                            : 'text-green-600 bg-green-100'
                                                    } p-1 rounded`}
                                                >
                                                    {t('thisCarIs')}
                                                </div>
                                            )}
                                            {desktopData[index]}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const MorInfo = ({
    inspectionRef,
    highlights,
    isRepainted,
    numberOfRepaintedPanels,
    kilometers,
    transmission,
    year,
}: {
    inspectionRef: React.RefObject<HTMLDivElement>;
    highlights: HighlightsType;
    isRepainted: boolean;
    numberOfRepaintedPanels: number;
    kilometers: string | number;
    year: number;
    transmission: string;
}) => {
    const { t } = useTranslation();
    return (
        <div className="pt-4 border-t border-gray-200 w-full">
            <div className="text-secondary-500 text-lg font-semibold leading-7">
                {t('carSummary')}
            </div>

            <div className="border border-solid border-gray-100 p-4 pt-5 mb-4 rounded-lg md:border-0 md:p-0 md:mb-0">
                <div className="hidden md:block mb-5 md:mb-0">
                    <CarProFileMoreInfoContainer>
                        <CarProFileMoreInfoItemContainer>
                            <Image
                                src={Speedometer2}
                                alt="Speedometer"
                                className="h-5 w-5"
                            />
                            <CarProfileMoreInfoText
                                variant={'body2'}
                            >{`${numberWithCommas(kilometers)} ${t(
                                'km'
                            )}`}</CarProfileMoreInfoText>
                        </CarProFileMoreInfoItemContainer>
                        <CarProFileMoreInfoItemContainer>
                            <Image
                                src={CalendarBookedCar2}
                                alt="CalendarCardIcon"
                                className="h-5 w-5"
                            />
                            <CarProfileMoreInfoText variant={'body2'}>
                                {year}
                            </CarProfileMoreInfoText>
                        </CarProFileMoreInfoItemContainer>
                        <CarProFileMoreInfoItemContainer>
                            <Image
                                src={TransmissionIcon2}
                                alt="TransmissionIcon"
                                className="h-5 w-5"
                            />
                            {/*
          @eslint-disable-next-line @typescript-eslint/ban-ts-comment
          @ts-ignore */}
                            <CarProfileMoreInfoText variant={'body2'}>
                                {t(transmission)}
                            </CarProfileMoreInfoText>
                        </CarProFileMoreInfoItemContainer>
                    </CarProFileMoreInfoContainer>
                </div>
                <div className="md:hidden flex flex-col gap-y-4">
                    <div className="flex gap-x-2 items-center">
                        <Image className="w-5 h-5" src={carFrame} alt="frame" />
                        {isRepainted ? (
                            numberOfRepaintedPanels > 3 ? (
                                <div className="text-gray-600 text-sm font-normal leading-tight">
                                    {t('fewRepaintedParts')}
                                </div>
                            ) : numberOfRepaintedPanels === 1 ? (
                                <div className="text-gray-600 text-sm font-normal leading-tight">
                                    {numberOfRepaintedPanels}{' '}
                                    {t('repaintedPart')}
                                </div>
                            ) : (
                                <div className="text-gray-600 text-sm font-normal leading-tight">
                                    {numberOfRepaintedPanels}{' '}
                                    {t('repaintedParts')}
                                </div>
                            )
                        ) : (
                            <div className="text-gray-600 text-sm font-normal leading-tight">
                                {t('noRepaintedParts')}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-x-2 items-center">
                        <Image className="w-5 h-5" src={engine} alt="engine" />

                        <div className="text-gray-600 text-sm font-normal leading-tight">
                            {t('noMechProblems')}
                        </div>
                    </div>
                    {highlights?.ar?.map((h, index) => (
                        <div
                            className="flex gap-x-2 items-center"
                            key={`${h}-highlight`}
                        >
                            <Image
                                className="w-5 h-5"
                                src={HighlightsIcon}
                                alt="star"
                            />
                            <div className="en:hidden text-gray-600 text-sm font-normal leading-tight">
                                {highlights?.ar[index]}
                            </div>
                            <div className="ar:hidden text-gray-600 text-sm font-normal leading-tight">
                                {highlights?.en[index]}
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={() => {
                            if (inspectionRef?.current) {
                                const targetPosition =
                                    inspectionRef?.current?.getBoundingClientRect()
                                        .top + window.scrollY;
                                window.scrollTo({
                                    top: targetPosition,
                                    behavior: 'smooth',
                                });
                            }
                        }}
                        className="border border-solid border-gray-300 rounded-lg py-2.5 px-4 justify-content-center items-center text-sm text-gray-700 font-medium leading-tight"
                    >
                        {t('Inspection Report')}
                    </button>
                </div>
            </div>
        </div>
    );
};
const PricingSection = ({
    sylndrSellingPrice,
    installmentPrice,
    discountPrice,
    carDetails,
    IS_DESKTOP,
}: {
    sylndrSellingPrice: string | number;
    installmentPrice: string | number;
    discountPrice: number;
    carDetails: CarDetailsType;
    IS_DESKTOP: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const carPriceSubSection = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
    };
    const carPriceSubSectionText = {
        display: 'flex',
        alignItems: 'baseline',
    };
    const hasDiscount = discountPrice !== undefined && discountPrice !== 0;

    const differencs = Math.trunc(
        parseFloat(sylndrSellingPrice as string) - discountPrice
    );

    const discountPercentage = formatNumber({ value: differencs });
    const displayedPrice = hasDiscount
        ? discountPrice.toString()
        : sylndrSellingPrice;

    // TEMP DISABLE FEATURE FLAG retail-price-breakdown.
    //const FEATURE_FLAG_PRICE_BREAKDOWN = 'retail-price-breakdown';

    // const [pricesExpirementIsActive, setPricesExpirementIsActive] =
    //     useState<boolean>(true);
    // const [isExpirementLoading, setIsExpirementLoading] = useState(true);

    const pricesExpirementIsActive = true;
    const isExpirementLoading = false;

    // TEMP DISABLE FEATURE FLAG.
    //const experimentPriceBreakdownType = ExpirementPriceBreakdown.PRICE_INCLUDED
    // const experimentPriceBreakdownType = useFeatureValue(
    //     FEATURE_FLAG_PRICE_BREAKDOWN,
    //     ExpirementPriceBreakdown.BINDING
    // ) as ExpirementPriceBreakdown;

    // TEMP DISABLE FEATURE FLAG.
    // // search this and change it. isPriceActive
    // const CheckIfPricesAreIncluded = () => {
    //     const isExpActive =
    //         experimentPriceBreakdownType ===
    //         ExpirementPriceBreakdown.PRICE_INCLUDED;
    //     setPricesExpirementIsActive(isExpActive);
    //     sylndrStorage.setItem('isPriceActive', JSON.stringify(isExpActive));
    // };

    // useEffect(() => {
    //     if (experimentPriceBreakdownType !== ExpirementPriceBreakdown.BINDING) {
    //         CheckIfPricesAreIncluded();
    //         setIsExpirementLoading(false);
    //         return;
    //     }
    //     const timer = setTimeout(() => {
    //         CheckIfPricesAreIncluded();
    //         setIsExpirementLoading(false);
    //     }, 2000);
    //
    //     return () => clearTimeout(timer);
    // }, [experimentPriceBreakdownType]);

    return (
        <div className={styles.priceSectionContainer}>
            {IS_DESKTOP && (
                <CarProfileSubtitle variant={'body2Medium'}>
                    {t('pricing')}
                </CarProfileSubtitle>
            )}
            <Conditional condition={hasDiscount}>
                <div className={styles.flexCenter}>
                    <div className={styles.displayFlex}>
                        <Typography
                            variant="caption"
                            className={`${styles.discountPrice} ${
                                i18n.language === 'ar'
                                    ? 'strikethroughAr'
                                    : 'strikethrough'
                            }`}
                        >
                            {`${numberWithCommas(sylndrSellingPrice)}`}
                        </Typography>
                        <Typography
                            className={styles.discountCurrency}
                            variant="caption"
                        >
                            {t('EGP')}
                        </Typography>
                    </div>
                    <div className={styles.discountPercentageContainer}>
                        <Typography
                            className={styles.discountPercentageValue}
                            variant="subtitle1SemiBold"
                        >
                            {`${discountPercentage}`}
                        </Typography>
                    </div>
                </div>
            </Conditional>
            <div style={carPriceSubSection}>
                <div style={carPriceSubSectionText}>
                    <CarProfileCurrency variant={'body2'}>
                        {t('EGP')}
                    </CarProfileCurrency>
                    <Typography
                        variant={'h6SemiBold'}
                        color={colors.secondary500}
                    >
                        {numberWithCommas(displayedPrice as string)}
                    </Typography>
                </div>
                {!!installmentPrice && (
                    <CarProfileInstallmentPrice variant={'body2Medium'}>
                        {t('from')} {numberWithCommas(installmentPrice)}
                        {t('/month')}
                    </CarProfileInstallmentPrice>
                )}
            </div>
            <div>
                <FlapperLine
                    className="bg-secondary-50 rounded-md md:block hidden"
                    hasDiscount={hasDiscount}
                    discountText={t('limitedOffer')}
                    tags={carDetails.tags}
                    lang={lang as AVAILABLE_LANGUAGES}
                />
            </div>
            <div className={styles.whatsIncluded}>
                {!isExpirementLoading &&
                    (pricesExpirementIsActive ? (
                        <PriceBreakdown
                            IS_DESKTOP={IS_DESKTOP}
                            carDetails={carDetails}
                            t={t}
                        />
                    ) : (
                        <WhatsIncluded
                            hideToolTip={!IS_DESKTOP}
                            carDetails={carDetails}
                            hasTrackEvent={true}
                            registrationFees={carDetails.registrationFees}
                        />
                    ))}
            </div>
        </div>
    );
};
const CarInfo = ({
    carInfo,
    carDetails,
    IS_DESKTOP,
    highlights,
    isRepainted,
    numberOfRepaintedPanels,
    inspectionRef,
    carDetailsOverview,
}: {
    highlights: HighlightsType;
    isRepainted: boolean;
    numberOfRepaintedPanels: number;
    inspectionRef: React.RefObject<HTMLDivElement>;
    carInfo: DisplayedCarInfoInterface;
    carDetails: CarDetailsType;
    IS_DESKTOP: boolean;
    carDetailsOverview: any[];
}) => {
    const { i18n, t } = useTranslation();
    const { width } = useWindowDimensions();
    const lang = i18n.language;
    const isDesktop = width > 850;
    const [notifyMeModal, setNotifyModal] = useState(false);
    const router = useRouter();

    const seeCarButtonClicked = () => {
        if (carInfo?.id === undefined) return;
        router.push(
            `/${i18n.language}/car-details/${carInfo?.id}/checkout/see`
        );
    };
    const carType = carDetails.category;
    const badgeConfig = useMemo(
        () => getCategoryBadgeConfig(carType, t),
        [carType, t]
    );
    const handleBadge = (): React.JSX.Element => {
        // Always show Lite/Certified badge
        const badgeText = badgeConfig.label;
        const badgeColor = badgeConfig.bgColor;
        const textColor = badgeConfig.textColor;

        const liteCertifiedBadge = (
            <span
                className="hidden md:flex flex-row gap-1 items-center"
                style={{
                    backgroundColor: badgeColor,
                    padding: '4px 8px',
                    borderRadius: '6px',
                }}
            >
                <img
                    src={myImageLoader({
                        src: badgeConfig.icon,
                        width: 16,
                        height: 16,
                    })}
                    alt={
                        carDetails?.isRefurbished
                            ? 'Sylndr Certified'
                            : `Sylndr ${t('Lite')}`
                    }
                    width={16}
                    height={16}
                />
                <Typography
                    variant="captionBold"
                    style={{
                        color: textColor,
                        fontSize: '12px',
                        fontWeight: 500,
                    }}
                >
                    {badgeText}
                </Typography>
            </span>
        );

        // Check if we need to show a status badge as well
        let statusBadge = null;

        if (carInfo.isSold) {
            statusBadge = <SoldBadge />;
        } else if (carInfo.isPurchaseInProgress || carInfo.isReserved) {
            const translations = getCarCardLocale();
            statusBadge = (
                <BadgeWithTooltip
                    translations={translations}
                    type={
                        carInfo.isPurchaseInProgress
                            ? EBadgeWithTooltip.PURCHASE_IN_PROGRESS
                            : EBadgeWithTooltip.RESERVED
                    }
                />
            );
        }

        // Return both badges in a container
        return (
            <div className="flex flex-row gap-2 items-center">
                {liteCertifiedBadge}
                {statusBadge}
            </div>
        );
    };

    return (
        <>
            <CarProfileInfoContainer>
                <div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            position: 'relative',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                position: 'relative',
                                flexDirection: 'row',
                                placeItems: 'center',
                                width: '100%',
                            }}
                        >
                            <CarProfileCarName
                                variant="subtitle2SemiBold"
                                component="h1"
                            >{`${carInfo?.make} ${carInfo?.model} ${carInfo?.year}`}</CarProfileCarName>
                            {handleBadge()}
                        </div>

                        <div className="md:pb-4">
                            <CarProfileTrim
                                variant={'body2'}
                                className="en:hidden"
                                component="h2"
                            >
                                {carInfo?.trim?.ar}
                            </CarProfileTrim>
                            <CarProfileTrim
                                variant={'body2'}
                                className="ar:hidden"
                            >
                                {carInfo?.trim?.en}
                            </CarProfileTrim>
                        </div>

                        <div className="md:hidden">
                            <CarDetailsSection data={carDetailsOverview} />
                        </div>

                        {(carInfo.isSold || carInfo.isPurchaseInProgress) &&
                            !isDesktop && (
                                <CarGuarantee
                                    isRefurbished={carDetails.isRefurbished}
                                />
                            )}

                        <div className="hidden md:block">
                            <MorInfo
                                inspectionRef={inspectionRef}
                                highlights={highlights}
                                isRepainted={isRepainted}
                                numberOfRepaintedPanels={
                                    numberOfRepaintedPanels
                                }
                                kilometers={carInfo?.kilometers}
                                year={carInfo?.year}
                                transmission={carInfo?.transmission}
                            />
                        </div>
                        {carInfo.isSold ||
                            (carInfo.isPurchaseInProgress && (
                                <div className="md:hidden">
                                    <MorInfo
                                        inspectionRef={inspectionRef}
                                        highlights={highlights}
                                        isRepainted={isRepainted}
                                        numberOfRepaintedPanels={
                                            numberOfRepaintedPanels
                                        }
                                        kilometers={carInfo?.kilometers}
                                        year={carInfo?.year}
                                        transmission={carInfo?.transmission}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
                <div className="md:block hidden">
                    {carInfo.showroom &&
                    !carInfo.isPurchaseInProgress &&
                    !carInfo.isSold ? (
                        <ShowroomSection
                            showroom={carInfo.showroom}
                            lang={lang as AVAILABLE_LANGUAGES}
                            isDesktop={isDesktop}
                            carInfo={carInfo}
                            setNotifyModal={setNotifyModal}
                            seeCarButtonClicked={seeCarButtonClicked}
                        />
                    ) : null}
                </div>
                {(isDesktop ||
                    !(carInfo?.isSold || carInfo.isPurchaseInProgress)) && (
                    <div className={styles.pricingContainer}>
                        {(carInfo?.isSold || carInfo.isPurchaseInProgress) &&
                            isDesktop && (
                                <CarSoldOverlay
                                    type={
                                        carInfo.isPurchaseInProgress
                                            ? 'PURCHASE_IN_PROGRESS'
                                            : 'SOLD'
                                    }
                                    style={{
                                        marginLeft: '-16px',
                                        marginRight: '-16px',
                                        marginBottom: '-24px',
                                        marginTop: '-16px',
                                    }}
                                />
                            )}
                        <PricingSection
                            IS_DESKTOP={IS_DESKTOP}
                            sylndrSellingPrice={carInfo?.sylndrSellingPrice}
                            installmentPrice={carInfo?.installmentPrice}
                            discountPrice={carInfo?.discountPrice}
                            carDetails={carDetails}
                        />

                        <div className="md:hidden">
                            {carInfo.showroom &&
                            !carInfo.isPurchaseInProgress &&
                            !carInfo.isSold ? (
                                <ShowroomSection
                                    showroom={carInfo.showroom}
                                    lang={lang as AVAILABLE_LANGUAGES}
                                    isDesktop={isDesktop}
                                    carInfo={carInfo}
                                    setNotifyModal={setNotifyModal}
                                    seeCarButtonClicked={seeCarButtonClicked}
                                />
                            ) : null}
                        </div>

                        <div className="md:hidden">
                            <CarGuarantee
                                isRefurbished={carDetails.isRefurbished}
                            />
                            <MorInfo
                                inspectionRef={inspectionRef}
                                highlights={highlights}
                                isRepainted={isRepainted}
                                numberOfRepaintedPanels={
                                    numberOfRepaintedPanels
                                }
                                kilometers={carInfo?.kilometers}
                                year={carInfo?.year}
                                transmission={carInfo?.transmission}
                            />
                        </div>
                        <div className="md:flex hidden md:flex-col md:gap-6">
                            <ReserveSeeNotify
                                carInfo={carInfo}
                                lang={lang}
                                setNotifyModal={setNotifyModal}
                                seeCarButtonClicked={seeCarButtonClicked}
                                showTip={true}
                            />
                        </div>
                        <Modal
                            open={notifyMeModal}
                            sx={{
                                backdropFilter: 'blur(6px)',
                                borderRadius: '12px',
                            }}
                            setOpen={setNotifyModal}
                        >
                            <NotifyMe
                                setModal={setNotifyModal}
                                carId={carInfo?.id}
                            />
                        </Modal>
                    </div>
                )}
            </CarProfileInfoContainer>
        </>
    );
};
export default CarInfo;
